import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import { QueryTable } from "../../../ui/components/Table";
import { useAuth } from "../../lib/auth/hooks";
import {
  fetchProductModels,
  createProductModel,
  updateProductModel,
} from "../../lib/api/products";
import type { ProductModel, ProductLine } from "../../lib/api/products";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams } from "../../../ui/api/listApi.types";
import { useI18n } from "../../lib/i18n";
import * as pageStyles from "../users/UsersListPage.css";
import * as s from "../organizations/OrganizationDetailPage.css";

const PRODUCT_LINE_LABELS: Record<ProductLine, string> = {
  sanctuary: "Sanctuary",
  peak: "Peak",
  sanctuary_1: "Sanctuary 1",
  sanctuary_2: "Sanctuary 2",
  sanctuary_3: "Sanctuary 3",
  vault: "Vault",
  cabinet: "Cabinet",
  container: "Container",
  summit: "Summit",
  safari: "Safari",
};

const COLUMNS: ColumnDefinition<ProductModel>[] = [
  {
    id: "name",
    label: "Name",
    width: 200,
    sortable: true,
    sortKey: "name",
    sort: (a, b) => a.name.localeCompare(b.name),
    render: (row) => <span style={{ fontWeight: 500 }}>{row.name}</span>,
  },
  {
    id: "productLine",
    label: "Product Line",
    width: 160,
    render: (row) => <span>{PRODUCT_LINE_LABELS[row.productLine] ?? row.productLine}</span>,
  },
  {
    id: "modelNumber",
    label: "Model #",
    width: 140,
    enableHiding: true,
    render: (row) => <span style={{ color: "#6B7280" }}>{row.modelNumber ?? "—"}</span>,
  },
  {
    id: "hasWifi",
    label: "WiFi",
    width: 80,
    render: (row) => <span>{row.hasWifi ? "✓" : "—"}</span>,
  },
  {
    id: "hasBluetooth",
    label: "BT",
    width: 60,
    render: (row) => <span>{row.hasBluetooth ? "✓" : "—"}</span>,
  },
  {
    id: "isActive",
    label: "Active",
    width: 90,
    filterKey: "isActive",
    filterLabel: "Active",
    filterType: "checkbox",
    filterOptions: () => [
      { value: "true", label: "Active" },
      { value: "false", label: "Inactive" },
    ],
    filterAccessor: (row) => String(row.isActive),
    render: (row) => (
      <span style={{ color: row.isActive ? "#16A34A" : "#9CA3AF", fontWeight: 600 }}>
        {row.isActive ? "Active" : "Inactive"}
      </span>
    ),
  },
];

type ModalState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; model: ProductModel };

export function ProductModelsPage() {
  const { t, tx } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");

  const [refreshKey, setRefreshKey] = React.useState(0);
  const [modalState, setModalState] = React.useState<ModalState>({ mode: "closed" });

  const fetchModels = React.useCallback(
    async (params: ListParams) => {
      if (!token) return { data: [] as ProductModel[], total: 0 };
      const rows = await fetchProductModels(token, true);
      let items = rows;
      if (params.search?.trim()) {
        const q = params.search.toLowerCase();
        items = items.filter(
          (m) =>
            m.name.toLowerCase().includes(q) ||
            m.productLine.includes(q) ||
            (m.modelNumber?.toLowerCase().includes(q) ?? false),
        );
      }
      return { data: items, total: items.length };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey],
  );

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">{tx("pro.products.modelsTitle")}</Text>
        <div className={pageStyles.pageHeaderActions}>
          <Button type="button" color="primary" onClick={() => setModalState({ mode: "create" })}>
            Add model
          </Button>
        </div>
      </div>

      <div className={pageStyles.tableHost}>
        <QueryTable<ProductModel>
          tableId="product-models"
          columns={COLUMNS}
          fetchList={fetchModels}
          contextKey={String(refreshKey)}
          fillContainer
          searchPlaceholder="Search by name or product line…"
          emptyTitle="No product models"
          emptySubtitle={t("pro.products.modelsEmptySubtitle")}
          defaultSort={{ sort_by: "name", order: "asc" }}
          onRowClick={(row) => setModalState({ mode: "edit", model: row })}
        />
      </div>

      <ProductModelModal
        state={modalState}
        onClose={() => setModalState({ mode: "closed" })}
        onSaved={() => {
          setModalState({ mode: "closed" });
          setRefreshKey((k) => k + 1);
        }}
        accessToken={token ?? ""}
      />
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ProductModelModalProps {
  state: ModalState;
  onClose: () => void;
  onSaved: () => void;
  accessToken: string;
}

interface ModelFormValues {
  name: string;
  productLine: ProductLine | "";
  modelNumber: string;
  description: string;
  hasWifi: boolean;
  hasBluetooth: boolean;
  isActive: boolean;
  smartCategoryProductId: string;
}

const emptyModelForm = (): ModelFormValues => ({
  name: "",
  productLine: "",
  modelNumber: "",
  description: "",
  hasWifi: false,
  hasBluetooth: false,
  isActive: true,
  smartCategoryProductId: "",
});

function modelToForm(m: ProductModel): ModelFormValues {
  return {
    name: m.name,
    productLine: m.productLine,
    modelNumber: m.modelNumber ?? "",
    description: m.description ?? "",
    hasWifi: m.hasWifi,
    hasBluetooth: m.hasBluetooth,
    isActive: m.isActive,
    smartCategoryProductId: m.smartCategoryProductId ?? "",
  };
}

function ProductModelModal({ state, onClose, onSaved, accessToken }: ProductModelModalProps) {
  const { t, tx } = useI18n();
  const isEdit = state.mode === "edit";
  const open = state.mode !== "closed";

  const [values, setValues] = React.useState<ModelFormValues>(emptyModelForm());
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) { setError(null); return; }
    setValues(isEdit && state.mode === "edit" ? modelToForm(state.model) : emptyModelForm());
  }, [open, state.mode]);

  const patch = (field: keyof ModelFormValues, value: string | boolean) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim() || !values.productLine) {
      setError("Name and product line are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit && state.mode === "edit") {
        await updateProductModel(accessToken, state.model.id, {
          name: values.name.trim(),
          modelNumber: values.modelNumber.trim() || null,
          description: values.description.trim() || null,
          hasWifi: values.hasWifi,
          hasBluetooth: values.hasBluetooth,
          isActive: values.isActive,
          smartCategoryProductId: values.smartCategoryProductId.trim() || null,
        });
      } else {
        await createProductModel(
          {
            productLine: values.productLine as ProductLine,
            name: values.name.trim(),
            modelNumber: values.modelNumber.trim() || null,
            description: values.description.trim() || null,
            hasWifi: values.hasWifi,
            hasBluetooth: values.hasBluetooth,
            isActive: values.isActive,
            smartCategoryProductId: values.smartCategoryProductId.trim() || null,
          },
          accessToken,
        );
      }
      onSaved();
    } catch (err: any) {
      setError(err?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} setOpen={onClose} minWidth="420px" maxWidth="520px" closeOnClickOff>
      <div style={{ padding: "24px 28px" }}>
        <Text level="h2" weight="bold" style={{ marginBottom: 20 }}>
          {isEdit ? "Edit product model" : "Add product model"}
        </Text>
        <form onSubmit={handleSave}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>
                Product line *
              </label>
              <select
                value={values.productLine}
                onChange={(e) => patch("productLine", e.target.value)}
                disabled={saving || isEdit}
                style={{
                  padding: "8px 10px", borderRadius: 6,
                  border: "1px solid #D1D5DB", fontSize: 14,
                  background: isEdit ? "#F3F4F6" : "var(--color-inputBg, #fff)",
                  color: "var(--color-fg, #111)",
                }}
              >
                <option value="">— Select —</option>
                {(Object.entries(PRODUCT_LINE_LABELS) as [ProductLine, string][]).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>

            <TextInput
              label={t("pro.products.nameRequired")}
              value={values.name}
              onChange={(e) => patch("name", e.target.value)}
              disabled={saving}
              autoFocus
            />
            <TextInput
              label={t("pro.products.modelNumber")}
              value={values.modelNumber}
              onChange={(e) => patch("modelNumber", e.target.value)}
              disabled={saving}
              placeholder="e.g. LS2-10KWH"
            />
            <TextInput
              label={t("pro.products.description")}
              value={values.description}
              onChange={(e) => patch("description", e.target.value)}
              disabled={saving}
            />
            <TextInput
              label={t("pro.products.smartCategoryProductId")}
              value={values.smartCategoryProductId}
              onChange={(e) => patch("smartCategoryProductId", e.target.value)}
              disabled={saving}
              placeholder="00000000-0000-0000-0000-000000000000"
            />

            <div style={{ display: "flex", gap: 20 }}>
              {(["hasWifi", "hasBluetooth", "isActive"] as const).map((field) => (
                <label key={field} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={values[field] as boolean}
                    onChange={(e) => patch(field, e.target.checked)}
                    disabled={saving}
                  />
                  {field === "hasWifi" ? "Has WiFi" : field === "hasBluetooth" ? "Has Bluetooth" : "Active"}
                </label>
              ))}
            </div>
          </div>

          {error && <div style={{ marginTop: 14 }}><Callout variant="error">{error}</Callout></div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
            <Button type="button" color="neutral" onClick={onClose} disabled={saving}>{tx("shared.common.cancel")}</Button>
            <Button type="submit" color="primary" disabled={saving || !values.name.trim() || !values.productLine}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add model"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
